import { apiFetch } from './apim-fetch';
import { getJwt } from './jwt-client';
import { getApimBaseUrl, readConfig } from './config';

const APP_ID = 'v1';

function base(): string {
  return `${getApimBaseUrl().replace(/\/$/, '')}/AuthEngine`;
}

/**
 * Retrieves the user's V1 config blob from AuthEngine.
 * Returns null if the user has no stored config (404).
 * Throws on other errors.
 */
export async function getConfig(): Promise<unknown | null> {
  try {
    return await apiFetch<unknown>({ url: `${base()}/user-settings/config/${APP_ID}` });
  } catch (err: unknown) {
    if (err instanceof Error && (err as any).message?.includes('404')) {
      return null;
    }
    throw err;
  }
}

/**
 * Persists the user's V1 config blob to AuthEngine.
 */
export async function putConfig(config: unknown): Promise<void> {
  if (!readConfig().isAuthenticated) {
    return;
  }
  await apiFetch<void>({
    url: `${base()}/user-settings/config/${APP_ID}`,
    method: 'PUT',
    data: config,
  });
}

export interface RemoteLeague {
  saveString: string;
  sportId: number;
}

/**
 * Returns all leagues stored for this user keyed by leagueGuid.
 */
export async function getLeagues(): Promise<Record<string, RemoteLeague>> {
  const resp = await apiFetch<{ leagues: Record<string, RemoteLeague> }>({
    url: `${base()}/user-settings/leagues`,
  });
  return resp.leagues ?? {};
}

/**
 * Persists a single league's saveString to AuthEngine.
 * When opts.keepalive is true, uses the browser's keepalive fetch so the
 * request completes even when the page is being unloaded.
 */
export async function putLeague(
  sportId: number,
  leagueGuid: string,
  saveString: string,
  opts?: { keepalive?: boolean },
): Promise<void> {
  if (!readConfig().isAuthenticated) {
    return;
  }
  const url = `${base()}/user-settings/leagues/${sportId}/${leagueGuid}`;
  if (opts?.keepalive) {
    const jwt = await getJwt();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (jwt !== null) {
      headers['Authorization'] = `Bearer ${jwt}`;
    }
    await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ saveString }),
      keepalive: true,
    });
    return;
  }
  await apiFetch<void>({ url, method: 'PUT', data: { saveString } });
}
